"use client";
// ===================================================
// GAM Command Center — "מצבים" (Environments) data layer
// ===================================================
// FOUR stages along the release flow (Gal's model):
//   Local      = Fast Work / Scratch — live Claude working tree → localhost (surface local_git). May be dirty/unsaved, not GitHub-backed.
//   Edit       = Committed Draft — saved commit, meant to stay on vdev until pushed → vbrain.win (surface github).
//                NOTE: facts currently read GitHub, NOT the true vdev rsync snapshot (no VPS SHA stamping yet).
//   Preview    = Vercel Preview — the pinned `preview` branch, built twice a day. Vercel keeps a
//                stable custom domain preview.vbrain.io (bound to the `preview` branch, 21.09), so the link
//                never changes per build. A custom domain does NOT bypass Vercel protection by itself:
//                this one is bound to gitBranch "preview", so Vercel counted it as a preview deployment
//                and SSO-gated it. Closed 22.09 by adding it to Deployment Protection Exceptions
//                (Gal approved), measured: /dashboard now 307 -> /auth/login -> /login 200, no vercel.com
//                hop. Our own app login is the only gate on it.
//                This is the stage Gal actually looks at before approving production.
//   Production = Vercel Production — manual Deploy only, from the exact SHA that was approved on
//                Preview → vbrain.io (surface vercel). Read-only status/link here.
// Vocabulary: Commit = saved state · Push = GitHub + VPS Preview · Deploy = Vercel Production ·
//             VPS = development + verification source · Vercel = final manual production target.
// Reads the SAME dev_status_items + dev_status_drift as DevStatusBoard — no new tables (Rule 10).
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export type EnvKey = "local" | "edit" | "preview" | "production";

export interface EnvItem {
  surface: string; item_kind: string; external_id: string; name: string;
  status: string | null; is_dirty: boolean | null;
  ahead: number | null; behind: number | null;
  metadata: Record<string, unknown> | null; deep_link: string | null;
  snapshot_at: string;
}
export interface EnvDrift { severity: string; message: string; surface: string; item: string }

// Static descriptor per stage — base URL + label + tagline + brand chip + honest limitation.
// label = short stage name (fits the compact bar); tagline = the role; limitation = caveat shown in UI.
// `preview` uses Vercel's stable per-branch alias, which always serves the newest READY build of
// the pinned `preview` branch — measured 20.09, when it served the exact SHA that was promoted.
export const ENVS: Record<EnvKey, { label: string; tagline: string; base: string; chip: string; limitation: string }> = {
  local:      { label: "Local · Scratch",        tagline: "Fast Work / Scratch",               base: "http://localhost:3000", chip: "text-amber-300 border-amber-500/40 bg-amber-500/10",   limitation: "חי רק על localhost / working tree מקומי. עלול להיות לא שמור (dirty) ולא מגובה ב-GitHub." },
  edit:       { label: "Edit · Committed Draft", tagline: "Committed Draft",                    base: "https://vbrain.win", chip: "text-teal-300 border-teal-500/40 bg-teal-500/10",     limitation: "מציג כרגע עובדות מ-GitHub — לא בהכרח ה-snapshot האמיתי של vdev (אין VPS SHA stamping עדיין)." },
  preview:    { label: "Preview · Vercel",       tagline: "Vercel Preview — שער האישור",        base: "https://preview.vbrain.io", chip: "text-sky-300 border-sky-500/40 bg-sky-500/10",     limitation: "נבנה פעמיים ביום מהענף preview, לא בכל push. ה-SHA כאן הוא בדיוק מה שיעלה ל-production אחרי אישור. הכתובת preview.vbrain.io קבועה, מגישה תמיד את הבנייה האחרונה של ענף preview, ונשמרת בדפדפן כ-origin נפרד. נמדד 22.09.2026: שער ההתחברות שלנו הוא היחיד על הכתובת. היא נוספה לרשימת Deployment Protection Exceptions ב-Vercel באישור גל, ולכן אינה מופנית עוד ל-SSO של Vercel." },
  production: { label: "Production · Vercel",     tagline: "Vercel Production",                  base: "https://vbrain.io",      chip: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", limitation: "קריאה-בלבד (status/link). ה-iframe עלול להיחסם ע\"י כותרות production. Deploy = פעולה ידנית נפרדת." },
};

export const envUrl = (env: EnvKey, route: string) => `${ENVS[env].base}${route}`;

// Core release-flow vocabulary — shown in help/compare UI only (no behavior).
export const ENV_VOCAB: { term: string; means: string }[] = [
  { term: "Commit", means: "מצב שמור (saved state)" },
  { term: "Push",   means: "GitHub + VPS Preview (לא deploy ל-Vercel)" },
  { term: "Stage",  means: "קידום הענף preview ל-SHA מאושר ובניית אותו SHA ב-Vercel Preview" },
  { term: "Deploy", means: "Vercel Production — ידני בלבד, רק מ-SHA שעבר ב-Preview" },
  { term: "VPS",    means: "מקור הפיתוח והאימות" },
  { term: "Vercel", means: "יעד ה-production הסופי — ידני בלבד" },
];

// Planning note (NOT implemented yet): the STAGE selector chooses Local/Edit/Preview/Production.
// A future Version / Source selector will choose branch / commit / PR / deployment WITHIN the selected stage.

// Version / Source plan (read-only, NOT implemented) — what a FUTURE per-stage
// source/version selector would choose INSIDE each stage. UI / help text only.
export const ENV_SOURCE_PLAN: Record<EnvKey, string> = {
  local:      "עץ עבודה פעיל / מצב dirty",
  edit:       "commit מקומי על ה-VPS / טיוטה שמורה",
  preview:    "ה-SHA המוצמד לענף preview",
  production: "deployment ב-Vercel / SHA פרוס",
};

// Honesty status per stage (read-only label only) — what the UI may imply.
// Vocabulary: עובד · חלקי · מתוכנן · קריא בלבד · לא מחובר עדיין.
export const ENV_STATUS: Record<EnvKey, { label: string; tone: "ok" | "partial" | "planned" | "readonly"; note?: string }> = {
  local:      { label: "חלקי",          tone: "partial",  note: "חי רק על localhost" },
  edit:       { label: "עובד",          tone: "ok" },
  preview:    { label: "חלקי",          tone: "partial",  note: "נבנה מ-SHA מוצמד · כתובת קבועה · דורש התחברות" },
  production: { label: "קריא בלבד",      tone: "readonly", note: "סטטוס / קישור · Deploy ידני בלבד" },
};

export function ago(iso?: string): string {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `לפני ${m} דק'`;
  if (m < 48 * 60) return `לפני ${Math.round(m / 60)} שע'`;
  return `לפני ${Math.round(m / 1440)} ימים`;
}
export const short = (sha?: unknown) => (typeof sha === "string" ? sha.slice(0, 7) : "—");

export function useEnvStates() {
  const [items, setItems] = useState<EnvItem[]>([]);
  const [drift, setDrift] = useState<EnvDrift[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [i, d] = await Promise.all([
        supabase.from("dev_status_items").select("*"),
        supabase.from("dev_status_drift").select("*"),
      ]);
      if (!alive) return;
      setItems((i.data as EnvItem[]) ?? []);
      setDrift((d.data as EnvDrift[]) ?? []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);
  return { items, drift, loading };
}

export interface EnvFacts { label: string; branch: string; commit: string; status: string; when: string; link: string | null; empty: boolean }

export function envFacts(env: EnvKey, items: EnvItem[]): EnvFacts {
  const by = (s: string) => items.filter((x) => x.surface === s);
  const freshOf = (s: string) => ago(by(s).map((x) => x.snapshot_at).sort().at(-1));
  const meta = (x?: EnvItem) => (x?.metadata as Record<string, unknown> | null);

  if (env === "local") {
    const local = by("local_git");
    const cur = local.find((x) => x.item_kind === "branch" && x.status === "current");
    const tree = local.find((x) => x.item_kind === "summary");
    return {
      label: ENVS.local.label, branch: cur?.name ?? "—",
      commit: short(meta(cur)?.sha ?? meta(tree)?.sha),
      status: tree?.is_dirty ? `${meta(tree)?.dirty_count ?? ""} לא שמורים` : "נקי",
      when: freshOf("local_git"), link: null, empty: local.length === 0,
    };
  }
  if (env === "edit") {
    const gh = by("github");
    const main = gh.find((x) => x.item_kind === "branch" && x.external_id === "main");
    const head = gh.find((x) => x.item_kind === "branch" && x.status === "current") ?? main;
    return {
      label: ENVS.edit.label, branch: head?.name ?? main?.name ?? "—",
      commit: short(meta(head)?.sha),
      status: gh.length === 0 ? "ממתין לטוקן" : "committed",
      when: freshOf("github"), link: head?.deep_link ?? null, empty: gh.length === 0,
    };
  }
  if (env === "preview") {
    const prev = by("vercel").find((x) => x.item_kind === "deployment" && meta(x)?.target === "preview");
    return {
      label: ENVS.preview.label, branch: String(meta(prev)?.ref ?? "—"),
      commit: short(meta(prev)?.sha),
      status: prev ? (prev.status ?? "—") : "אין build",
      when: prev ? ago(prev.snapshot_at) : "—", link: prev?.deep_link ?? null, empty: !prev,
    };
  }
  const vc = by("vercel");
  const prod = vc.find((x) => x.item_kind === "summary" && x.external_id === "production");
  return {
    label: ENVS.production.label, branch: String(meta(prod)?.ref ?? "main"),
    commit: short(meta(prod)?.sha),
    status: vc.length === 0 ? "ממתין לטוקן" : (prod?.status ?? "—"),
    when: freshOf("vercel"), link: prod?.deep_link ?? null, empty: vc.length === 0,
  };
}
